import React from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingCart, Heart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';

const WishlistPage = () => {
  const { wishlist, loading, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();

  const handleAddToCart = (item) => {
    addToCart(item._id || item.id, 1, item.name);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (wishlist.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto bg-[#D4AF37]/20 rounded-full flex items-center justify-center mb-6 border border-[#D4AF37]/50">
            <Heart className="h-12 w-12 text-[#D4AF37]" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4 font-heading">Your Wishlist is Empty</h2>
          <p className="text-[#888888] mb-8 font-body text-lg">Save items you love by clicking the heart icon.</p>
          <Link to="/shop">
            <Button 
              className="px-10 py-6 text-lg font-bold font-body rounded-full shadow-lg hover:shadow-xl transition-all bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A]"
            >
              Start Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-white mb-4 font-heading">My Wishlist</h1>
        <p className="text-[#888888] mb-8 font-body">{wishlist.length} items saved</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {wishlist.map((item) => {
            const displayPrice = item.sale_price || item.price || 0;
            const hasDiscount = item.sale_price && item.sale_price < item.price;

            return (
              <div key={item._id || item.id} className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 shadow-lg overflow-hidden group hover:border-[#D4AF37]/50 transition-colors">
                {/* Image */}
                <div className="relative aspect-square bg-[#1A1A1A]">
                  <Link to={`/shop/product/${item.slug}`}>
                    <img
                      src={item.images?.[0] || 'https://via.placeholder.com/300'}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </Link>
                  <button
                    onClick={() => removeFromWishlist(item._id || item.id)}
                    className="absolute top-3 right-3 p-2 bg-[#0A0A0A]/80 rounded-full shadow-lg hover:bg-red-500 hover:text-white transition-colors text-white border border-[#D4AF37]/30"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  {hasDiscount && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 text-xs font-bold rounded-full">
                      SALE
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="p-4">
                  <Link to={`/shop/product/${item.slug}`}>
                    <h3 className="font-semibold text-white mb-2 hover:text-[#D4AF37] transition-colors font-body line-clamp-2">
                      {item.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl font-bold text-[#D4AF37] font-body">
                      ${displayPrice.toFixed(2)}
                    </span>
                    {hasDiscount && (
                      <span className="text-sm text-[#888888] line-through font-body">
                        ${item.price?.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <Button
                    onClick={() => handleAddToCart(item)}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] font-bold font-body"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Add to Cart
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WishlistPage;
